import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    InputAdornment,
    Alert,
    IconButton
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import {
    selectPlaceRoutePointOnClick,
    selectWorkingAddress,
    selectCurrentlyEditingPointType,
    selectWorkingLatLng,
    setStartPoint,
    setEndPoint,
    addViaPoint,
    selectRouteType,
    setIsMinimized,
    resetRouteDetails,
    selectCurrentWizardStep,
    selectPointSelectionMethod,
    setWorkingLatLng,
    setPlaceRoutePointOnClick
} from '../../../features/tripcenter/googleRouteMakerSlice';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { setRouteMaker } from '../../../features/tripcenter/routeSlice';

const getRouteTypeLabel = (routeType) => {
    switch (routeType) {
        case 'oneWayDirect':
            return 'One Way (Direct)';
        case 'roundTrip':
            return 'Round Trip';
        case 'oneWayWithStops':
            return 'One Way with Stops';
        case 'roundTripWithStops':
            return 'Round Trip with Stops';
        default:
            return 'Unknown Route Type';
    }
};

const CompactRoutePanel = () => {
    const dispatch = useDispatch();
    const routeType = useSelector(selectRouteType);
    const workingLatLng = useSelector(selectWorkingLatLng);
    const workingAddress = useSelector(selectWorkingAddress);
    const currentlyEditingPointType = useSelector(selectCurrentlyEditingPointType);
    const placeRoutePointOnClick = useSelector(selectPlaceRoutePointOnClick);
    const layout = useSelector(liveTrackinglayoutSelector);
    const currentStep = useSelector(selectCurrentWizardStep);

    // Get selection method from pointSelectionMethod selector
    const selectionMethod = useSelector(selectPointSelectionMethod);
    // Add states for location parameters
    const [locationParams, setLocationParams] = useState({
        name: '',
        triggerRadius: 200,
        maxDetentionTime: 0
    });

    const [errors, setErrors] = useState({});

    // Reset form when starting new location selection
    useEffect(() => {
        if (placeRoutePointOnClick) {
            setLocationParams({
                name: '',
                triggerRadius: 200,
                maxDetentionTime: 1
            });
            setErrors({});
        }
    }, [placeRoutePointOnClick]);

    // Update location name when address is received
    useEffect(() => {
        if (workingAddress) {
            setLocationParams(prev => ({
                ...prev,
                name: workingAddress.substring(0, 30) // Truncate long addresses
            }));
        }
    }, [workingAddress]);

    const handleParamChange = (field) => (event) => {
        setLocationParams(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        setErrors(prev => ({
            ...prev,
            [field]: false
        }));
    };

    const validateForm = () => {
        const newErrors = {
            name: !locationParams.name,
            triggerRadius: locationParams.triggerRadius < 200,
            maxDetentionTime: locationParams.maxDetentionTime < 0
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    const handleConfirmLocation = () => {
        if (!validateForm()) return;

        const locationData = {
            lat: workingLatLng.lat,
            lng: workingLatLng.lng,
            location: workingLatLng,
            address: workingAddress,
            locationName: locationParams.name,
            triggerRadius: Number(locationParams.triggerRadius),
            maxDetentionTime: Number(locationParams.maxDetentionTime),
            locationType: 'point'
        };
        // Dispatch appropriate action based on point type
        switch (currentlyEditingPointType) {
            case 'start':
                dispatch(setStartPoint(locationData));
                break;
            case 'end':
                dispatch(setEndPoint(locationData));
                break;
            case 'via':
                dispatch(addViaPoint(locationData));
                break;
            default:
                break;
        }

        // Clear working lat/lng
        dispatch(setWorkingLatLng(null));
        // Turn off map selection mode
        dispatch(setPlaceRoutePointOnClick(false));
        // Return to main modal
        dispatch(setIsMinimized(false));
    };
    const getHelperMessage = () => {
        // Get location type (start/end/via) for the message
        const locationTypeLabel = currentlyEditingPointType === 'via'
            ? 'stop'
            : currentlyEditingPointType;


        if (selectionMethod === 'vehicle') {
            return `You can adjust the ${locationTypeLabel} location by dragging the Red Circle marker on the map.`;
        }

        if (selectionMethod === 'map') {
            if (!workingLatLng) {
                return `Click anywhere on the map to select your ${locationTypeLabel} location.`;
            }
            return `You can adjust the ${locationTypeLabel} location by dragging the Red Circle marker on the map.`;
        }

        // Fallback message if we somehow get here without a selection method
        return workingLatLng
            ? workingAddress || 'Location selected'
            : '';
    };

    const getSelectionMethodLabel = () => {
        switch (selectionMethod) {
            case 'vehicle':
                return 'Vehicle Location';
            case 'map':
                return 'Map Location';
            default:
                return 'Unknown Selection Method';
        }
    };

    const handleMaximize = () => {
        dispatch(setIsMinimized(false));
    };

    const handleClose = () => {
        dispatch(resetRouteDetails());
        dispatch(setRouteMaker(null));
    };

    const renderContent = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 1 }}>
            {/* Route Type Header */}
            <Typography variant="h6">
                Route Type: {getRouteTypeLabel(routeType)}
            </Typography>

            {/* Current Action Status */}
            <Typography variant="subtitle1" color="primary">
                Setting {currentlyEditingPointType.toUpperCase()} location using {getSelectionMethodLabel()}
            </Typography>
            <Alert
                icon={<AssistantDirectionIcon />}
                severity={"error"}
            >
                {getHelperMessage()}
            </Alert>
            {
                workingAddress && (
                    <Alert
                        icon={<LocationOnIcon />}
                        severity={"info"}
                    >
                        {workingAddress}
                    </Alert>
                )
            }


            {/* Parameters Form */}
            {workingLatLng && (
                <>
                    <TextField
                        label="Location Name"
                        value={locationParams.name}
                        onChange={handleParamChange('name')}
                        error={errors.name}
                        helperText={errors.name ? "Name is required" : ""}
                        size="small"
                        fullWidth
                    />

                    <TextField
                        label="Trigger Radius"
                        type="number"
                        value={locationParams.triggerRadius}
                        onChange={handleParamChange('triggerRadius')}
                        error={errors.triggerRadius}
                        helperText={errors.triggerRadius ? "Minimum 200 meters" : ""}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        }}
                        size="small"
                        fullWidth
                    />

                    <TextField
                        label="Max Detention Time"
                        type="number"
                        value={locationParams.maxDetentionTime}
                        onChange={handleParamChange('maxDetentionTime')}
                        error={errors.maxDetentionTime}
                        helperText={errors.maxDetentionTime ? "Must be 0 or greater" : ""}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">hrs</InputAdornment>,
                        }}
                        size="small"
                        fullWidth
                    />

                    <Button
                        variant="contained"
                        onClick={handleConfirmLocation}
                        fullWidth
                    >
                        Confirm Location
                    </Button>
                </>
            )}
        </Box>
    );

    return (
        <Paper
            elevation={3}
            sx={{
                ...layout.lowersidebar,
                borderRadius: '8px',
            }}
        >
            {/* Header - remove border radius from header since Paper handles it */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
            }}>
                <Typography variant="subtitle1">
                    {currentStep === 'locations' ? 'Location Setup' : 'Route Builder'}
                </Typography>
                <Box>
                    <IconButton
                        onClick={handleMaximize}
                        color="inherit"
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        <OpenInFullIcon />
                    </IconButton>
                    <IconButton
                        onClick={handleClose}
                        color="inherit"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: 2 }}>
                {renderContent()}
            </Box>
        </Paper>
    );
};

export default CompactRoutePanel; 